import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { ReasonCode } from "@app/enum/ReasonCode";
import { IPlot } from "@app/interfaces/IPlot";
import { IVideoEntry } from "@app/main/components/videos/models/IVideoEntry";
import { VideoEntryService } from "@app/main/components/videos/services/video-entry.service";
import { CameraCommunicationServer, IDeviceInfo } from "@app/services/cameraCommunicationServer.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { PlotService } from "@app/services/plot.service";
import { ICheck } from "../../_models/ICheck";
import './diagnostics-4g.component.scss';

@Component({
    selector: 'diagnostics-4g',
    template: require('./diagnostics-4g.component.html')
})
export class Diagnostics4gComponent implements OnChanges {
    @Input() serial: string;
    @Input() vehicleId: number;

    plot: IPlot;
    valkVideoEntry: IVideoEntry;
    device: IDeviceInfo;
    checks: { string: ICheck } | {};

    processing: boolean;
    diagnosticsFailed: boolean = false;

    roadview = /ROADVIEW/i;
    connexas = /CONNEXAS/i;
    uat = /UAT/i;
    dev = /DEV/i;

    @Output() processingChanged = new EventEmitter<boolean>();

    constructor(
        private readonly cameraCommunicationServer: CameraCommunicationServer,
        private readonly videoEntryService: VideoEntryService,
        private readonly dateService: DateService,
        private readonly plotService: PlotService) { }

    
    ngOnChanges(): void {
        this.refreshDiagnosticsTests();
    }

    refreshDiagnosticsTests(){
        if (!this.serial) return;

        this.checks = {};

        this.processing = true;
        this.setCheckLabels("Checking");

        this.processingChanged.emit(this.processing);
        this.getData();
    }

    private getData() {
        (this.cameraCommunicationServer
            .getDeviceInfo(this.serial) as Promise<IDeviceInfo>)
            .then((deviceInfo: IDeviceInfo) => {
                if (!deviceInfo) {
                    this.diagnosticsFailed = true;
                }
                else {
                    this.device = deviceInfo;

                    return Promise.all([
                        this.plotService.getLatestPlotForVehicle(this.vehicleId) as Promise<IPlot>,
                        this.getValkVideoEntry(75, this.parseExternalId(this.device.CameraLatestVideos.length != 0 ? this.device.CameraLatestVideos[0].FileName : "")) as Promise<IVideoEntry>,
                    ]);
                }
            })
            .then(([plot, valkVideoEntry]) => {
                this.plot = plot
                this.valkVideoEntry = valkVideoEntry;
            })
            .catch((error) => {
                this.diagnosticsFailed = true;
            })
            .finally(() => {
                this.processing = false;

                    this.processingChanged.emit(this.processing)

                if (this.device) {
                    this.validate4Gdevice();
                }
                else {
                    this.checks = {};
                }
            });
    }

    private setCheckLabels(action: "Check" | "Checking"): void {

        [
            ["pulsePacket", "Diagnostics data"],
            ["gpsAccuracy", "GPS accuracy"],
            ["receivingDelay", "Receiving delay"],
            ["lastIgnition", "Last ignition"],
            ["operator", "Operator"],
            ["sdCard", "SD card"],
            ["camerasCount", "Cameras count"],
            ["videos", "Videos"],
            ["firmware", "Firmware"],
            ["config", "Config"],
        ].forEach((label) => {
            this.checks[label[0]] = {
                loadingLabel: `${action} ${label[1]}`,
            };
        });
    }

    private validate4Gdevice(): void {
        this.checks = {
            pulsePacket: this.processPulsePacket(this.device, this.plot),
            gpsAccuracy: this.processGPSAccuracy(this.device),
            receivingDelay: this.processReceivingDelay(this.plot),
            lastIgnition: this.processLastIgnitionData(this.device, this.plot),
            operator: this.processOperator(this.device),
            sdCard: this.processSDcard(this.device),
            camerasCount: this.processCamerasCount(this.device),
            videos: this.processVideo(this.valkVideoEntry, this.device),
            firmware: this.processFirmware(this.device),
            config: this.processConfig(this.device),
        };
    }

    private processPulsePacket(device: IDeviceInfo, plot: IPlot): ICheck {
        if (!device.LastConnected) {
            return {
                failedLabel: "No data"
            };
        }

        const packetDelayMinutes = this.dateService.getDiff(new Date(), device.LastConnected, DateUnit.Min);
        const plotDelayMinutes = plot ? this.dateService.getDiff(new Date(), plot.Utc, DateUnit.Min) : false;

        switch (true) {
            case packetDelayMinutes > 35:
                return {
                    warningLabel: `Diagnostics data last received ${this.dateService.formatDate(device.LastConnected)}`,
                    subText: `Possible causes: Camera is offline, SIM is blocked, updater crashed`,
                };

            case packetDelayMinutes < 35 && plotDelayMinutes && plotDelayMinutes > 90:
                return {
                    subText: "Telematic app is crashed or API is down"
                };

            case packetDelayMinutes < 35 && plotDelayMinutes && plotDelayMinutes < 90 && plot.Reason === ReasonCode.Wakeup:
                this.dateService.getDiff(plot.Utc, device.LastIgnitionChangeTime, DateUnit.Hour);
                if ((this.dateService.getDiff(plot.Utc, device.LastIgnitionChangeTime, DateUnit.Hour)) > 12) {
                    return {
                        failedLabel: "Diagnostics data is outdated",
                        subText: `Possible issues: wrong camera installation, ignition wire is connected directly to the battery`,
                    }
                }
        }

        return {
            successLabel: "Diagnostics data is up to date",
            subText: `Diagnostics data last received ${this.dateService.formatDate(device.LastConnected)}`
        }
    }

    private processGPSAccuracy(device: IDeviceInfo): ICheck {
        if (device.SatCount < 4 || !device.GPSAccuracy || device.GPSAccuracy > 15 || (!device.Lat || !device.Lon)) {

            return {
                failedLabel: "GPS is inaccurate",
                subText: `Poor signal: ${device.SatCount} satellites, ${device.GPSAccuracy}m accuracy`
            }
        }

        if (device.SatCount < 5 || !device.GPSAccuracy || device.GPSAccuracy > 10 || (!device.Lat || !device.Lon)) {
            return {
                warningLabel: "GPS potentially inaccurate",
                subText: `${device.SatCount} satellites, ${device.GPSAccuracy}m accuracy`
            }
        }

        return {
            successLabel: "GPS is accurate"
        }
    }

    private processReceivingDelay(plot: IPlot): ICheck {
        if (!plot) {
            return {
                failedLabel: "Plot data missing",
                subText: "Plot data missing on Verilocation"
            }
        }
        
        const delay = this.dateService.getDiff(plot.UtcHost, plot.Utc, DateUnit.Sec);

        switch (true) {
            case delay <= (2.5 * 60): // 150 seconds/2.5 minutes
                return {
                    successLabel: "Plot data delay",
                    subText: "Less than 2.5 minutes"
                }

            case delay >= (2.5 * 60) && delay <= (5 * 60):
                return {
                    warningLabel: "Plot data delay",
                    subText: "More than 2.5 minutes"
                };

            case delay > (5 * 60):
                return {
                    failedLabel: "Plot data delay",
                    subText: "More than 5 minutes"
                };
        }
    }

    private processLastIgnitionData(device: IDeviceInfo, plot: IPlot): ICheck {

        switch (true) {
            
        
            case plot && device.LastIgnitionChangeTime && (!plot.IsIgnitionOn || this.dateService.getDiff(plot.Utc, device.LastIgnitionChangeTime, DateUnit.Hour) <= 12):
                return {
                    successLabel: `Ignition OK`,
                    subText: `Last went ${plot.IsIgnitionOn ? 'on' : 'off'} - ${this.dateService.relativeTime(plot.Utc)}`
                }
                
            case plot && !!plot.IsIgnitionOn && this.dateService.getDiff(plot.Utc, device.LastIgnitionChangeTime, DateUnit.Hour) > 12:
                return {
                    failedLabel: `Ignition: possible wiring issue`,
                    subText: `Last went ${plot.IsIgnitionOn ? 'on' : 'off'} - more than 12 hours ago`
                }

            default:
                return {
                    warningLabel: "Ignition status unknown",
                    subText: `Some data is missing that is required for a complete check.  Last seen ignition change was ${this.dateService.relativeTime(device.LastIgnitionChangeTime || plot?.Utc)}`
                }
        }
    }

    private processOperator(device: IDeviceInfo): ICheck {
        if (!device.NetworkOperator) {
            return {
                infoLabel: `No network data`
            };
        }

        return {
            infoLabel: `Operator: ${device.NetworkOperator}`
        };
    }

    private processSDcard(device: IDeviceInfo): ICheck {
        const { SDCardSerial, SDCardAvailableMB, SDCardSizeMB, SDCardLabel } = device;

        switch (true) {
            case !SDCardSerial:
                return {
                    failedLabel: "SD card not detected",
                    subText: "Make sure an SD card is installed.",
                };

            case !SDCardLabel.includes("128") && !SDCardLabel.includes("256"):
                return {
                    failedLabel: `${SDCardLabel}  card installed`,
                    subText: "Required SD card capacity is 128GB or 256GB",
                };

            case SDCardAvailableMB <= SDCardSizeMB * 0.02:
                return {
                    warningLabel: "SD card: available space is below 2%",
                    subText: "Please send a reboot command to free space",
                };


            default:
                return {
                    successLabel: `SD card: ${this.device.SDCardLabel} (${Math.round(100 / this.device.SDCardSizeMB * this.device.SDCardAvailableMB)}% free)`,
                };
        }
    }

    private processCamerasCount(device: IDeviceInfo): ICheck {
        if (!device.AvailableChannels) {
            return { failedLabel: "No cameras detected" };
        }

        const camCount = device.AvailableChannels.split(",").length;

        if (camCount == 0) {
            return {
                warningLabel: "0 cameras channels found",
                subText: "The camera requires at least 1 active channel"
            }
        }
        const infoLabel = camCount == 1 ? "1 camera" : `${camCount} cameras`;

        return {
            infoLabel
        }
    }

    private processVideo(videoEntry: null | IVideoEntry, device: IDeviceInfo): ICheck {
        if (videoEntry !== null && videoEntry.Status == 7) { // 7 - downloaded
            return {
                successLabel: "Videos",
                subText: "Latest video available on Verilocation"
            }
        }
        if (videoEntry === null) {

            if (device.CameraLatestVideos.length > 0) {
                return {
                    warningLabel: "Videos",
                    subText: `No uploaded videos found on Verilocation, ${this.checkProdConfigName(device.ConfigName) ? "Camera is pointed to Production" : "Camera isn't pointed to Production"}`
                }
            }
            else {
                return {
                    failedLabel: "Videos",
                    subText: "Camera did not upload any video"
                }
            }

        }

        return {
            failedLabel: "Videos",
            subText: "Latest video isn't uploaded on Verilocation"
        }
    }

    private parseExternalId(fileNameWithExtension: string): string {
        let [fileName, extension] = fileNameWithExtension.split('.');
        let [fileNameWOChannelSuffix] = fileName.split('_CH');
        return fileNameWOChannelSuffix;
    }

    private checkProdConfigName(deviceConfigName: string): boolean {

        return deviceConfigName.includes("ProdLive");
    }

    private getValkVideoEntry(productGroupId: number, externalId: string): angular.IPromise<IVideoEntry> {
        return this.videoEntryService
            .getVideoEntryByProductGroupIdAndExternalId(productGroupId, externalId)
            .then((videos) => {
                return videos;
            }) as angular.IPromise<IVideoEntry>;
    }

    private processFirmware(device: IDeviceInfo): ICheck {
        const {
            ProdUpdaterId,
            ProdTelematicsId,
            UpdaterId,
            TelematicsId,
            TelematicsRequestedId,
            UpdaterRequestedId,
            IsAutoDeployment,
        } = device;

        switch (true) {
            case !UpdaterId && !UpdaterRequestedId && !TelematicsId && !TelematicsRequestedId:
                return {
                    failedLabel: "Firmware is missing",
                };

            case !IsAutoDeployment && !!UpdaterId && !TelematicsId:
                return {
                    failedLabel: "Telematics is missing",
                };

            case !IsAutoDeployment && !UpdaterId && !!TelematicsId:
                return {
                    failedLabel: "Updater is missing",
                };

            case !IsAutoDeployment && !!UpdaterId && !!TelematicsId:
                return {
                    successLabel: "Firmware is installed",
                };

            case !!IsAutoDeployment &&
                UpdaterId === ProdUpdaterId &&
                TelematicsId === ProdTelematicsId:
                return {
                    successLabel: "Firmware is up to date",
                };

            case !!IsAutoDeployment && UpdaterId !== ProdUpdaterId && TelematicsId !== ProdTelematicsId:
                return {
                    warningLabel: "Firmware is obsolete",
                };

            case !!IsAutoDeployment && UpdaterId === ProdUpdaterId && TelematicsId !== ProdTelematicsId:
                return {
                    warningLabel: "Telematics is obsolete",
                };

            case !!IsAutoDeployment && UpdaterId !== ProdUpdaterId && TelematicsId === ProdTelematicsId:
                return {
                    warningLabel: "Updater is obsolete",
                };
        }
    }

    private processConfig(device: IDeviceInfo): ICheck {
        const { ConfigId, ConfigName } = device;

        switch (true) {
            case !ConfigId:
                return { failedLabel: "Config is missing" };

            case (!this.roadview.test(ConfigName) && !this.connexas.test(ConfigName)) || this.uat.test(ConfigName) || this.dev.test(ConfigName):
                return {
                    warningLabel: "Config is invalid",
                };

            default:
                return { successLabel: "Config is valid" };
        }
    }
}
